import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/Button/Button";
import { PasswordInput } from "../components/PasswordInput/PasswordInput";
import "./AccountDeletionPage.css";

type Options = { owner_transfer_required: boolean; can_request_workspace_deletion: boolean; retention_notice: string; subscription_notice: string };
type Member = { id: string; tenant_id: string; name: string; role: string; status: string };
class RequestError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

// Dedicated deletion resource: credentials stay in memory, no existing browser
// session or configurable API origin is reused by this reauthentication flow.
async function request<T>(path: string, token = "", body?: object): Promise<T> {
  const response = await fetch(path, {
    method: body ? "POST" : "GET", credentials: "omit", redirect: "error",
    signal: AbortSignal.timeout(20000),
    headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json();
  if (!response.ok) throw new RequestError(response.status, typeof payload.error === "string" ? payload.error : "The request could not be completed.");
  return payload as T;
}

export function AccountDeletionPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [options, setOptions] = useState<Options | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [successor, setSuccessor] = useState("");
  const [scope, setScope] = useState("account");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [finished, setFinished] = useState(false);
  const [unknown, setUnknown] = useState(false);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setMessage("");
    try {
      const login = await request<{ token: string }>("/api/auth/login", "", { email: email.trim(), password });
      setPassword("");
      if (!login.token) throw new Error("Missing session");
      const me = await request<{ user: Member; tenant: { id: string }; feature_tenant_id: string }>("/api/auth/me", login.token);
      if (!me.user?.tenant_id || me.tenant?.id !== me.user.tenant_id || me.feature_tenant_id !== me.user.tenant_id || !["owner", "manager", "staff"].includes(me.user.role)) throw new Error("Workspace could not be verified");
      const controls = await request<Options>("/api/auth/account/deletion", login.token);
      if (typeof controls.owner_transfer_required !== "boolean" || typeof controls.retention_notice !== "string") throw new Error("Account controls unavailable");
      if (controls.owner_transfer_required) {
        const team = await request<{ users: Member[] }>("/api/users?limit=100", login.token);
        setMembers(team.users.filter((m) => m.tenant_id === me.user.tenant_id && m.id !== me.user.id && m.status === "active" && ["owner", "manager", "staff"].includes(m.role)));
      }
      setToken(login.token); setOptions(controls);
    } catch (error) {
      setMessage(error instanceof RequestError ? error.message : "Could not verify your account. Try again or recover your password.");
    } finally { setPassword(""); setBusy(false); }
  }

  async function remove(event: FormEvent) {
    event.preventDefault();
    if (busy || confirmation !== "DELETE" || !password || unknown || finished) return;
    setBusy(true); setMessage("");
    const entered = password; setPassword("");
    try {
      const result = await request<{ status: string; due_at?: string; message: string }>("/api/auth/account/deletion", token, {
        current_password: entered, confirmation: "DELETE", scope,
        ...(scope === "account" && successor ? { transfer_to_user_id: successor } : {}),
      });
      if (!["account_deleted", "workspace_deletion_requested"].includes(result.status)) throw new Error("Unknown result");
      setFinished(true); setToken("");
      setMessage(`${result.message}${result.due_at ? ` Due: ${result.due_at}.` : ""}`);
    } catch (error) {
      if (error instanceof RequestError && error.status < 500 && error.status !== 408) setMessage(error.message);
      else { setUnknown(true); setToken(""); setMessage("The result could not be confirmed. Sign in again to check your account before submitting another request."); }
    } finally { setBusy(false); }
  }

  return <main className="account-deletion"><section aria-labelledby="deletion-title">
    <p className="account-deletion-brand">LeadCat</p>
    <h1 id="deletion-title">Account and data deletion</h1>
    <p>Use this page even if you no longer have the LeadCat mobile app. Verify your account to delete your login profile or request deletion of a workspace you own.</p>
    <p>Deleting your login does not cancel billing. Manage subscriptions with the original payment provider. Shared business records remain with the workspace; required billing, legal and security evidence is retained under our retention policy.</p>
    {!options && !finished && <form onSubmit={signIn}>
      <label>Email address<input type="email" autoComplete="username" value={email} disabled={busy} required onChange={(e) => setEmail(e.target.value)} /></label>
      <label>Password<PasswordInput value={password} autoComplete="current-password" disabled={busy} required onChange={(e) => setPassword(e.target.value)} /></label>
      <Button type="submit" variant="leadcat" loading={busy}>Verify account</Button>
      <Link to="/forgot-password">Recover your password</Link>
    </form>}
    {options && !finished && !unknown && <form onSubmit={remove}>
      <p>{options.retention_notice}</p><p>{options.subscription_notice}</p>
      {options.can_request_workspace_deletion && <fieldset><legend>Deletion scope</legend>
        <Button disabled={busy} aria-pressed={scope === "account"} onClick={() => setScope("account")}>My account</Button>
        <Button disabled={busy} aria-pressed={scope === "workspace"} onClick={() => setScope("workspace")}>Workspace and operational data</Button>
      </fieldset>}
      {scope === "account" && options.owner_transfer_required && <fieldset><legend>Transfer ownership to an active member</legend>
        {members.map((m) => <Button key={m.id} disabled={busy} aria-pressed={successor === m.id} onClick={() => setSuccessor(m.id)}>{m.name} · {m.role}</Button>)}
        {!members.length && <p>No other active member is available. Choose workspace deletion to remove the whole workspace.</p>}
      </fieldset>}
      {scope === "workspace" && <p>This requests deletion for all workspace members. Processing may require subscription cancellation and physical media cleanup. The response will give the request deadline.</p>}
      <label>Current password<PasswordInput value={password} required disabled={busy} autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} /></label>
      <label>Type DELETE to confirm<input value={confirmation} required disabled={busy} autoComplete="off" onChange={(e) => setConfirmation(e.target.value)} /></label>
      <Button type="submit" variant="danger" loading={busy} disabled={confirmation !== "DELETE" || (scope === "account" && options.owner_transfer_required && !successor)}>{scope === "workspace" ? "Request workspace deletion" : "Delete my account"}</Button>
    </form>}
    {message && <p role="status">{message}</p>}
    {unknown && <Link reloadDocument to="/account-deletion">Verify account again</Link>}
    <Link to="/login">Return to sign in</Link>
  </section></main>;
}
